import { useState, useEffect } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Button from '../../shared/Button';
import TextFieldCardCounter from '../../shared/TextFieldCardCounter';
import TextEditor from '../../shared/TextEditor';
import Modal from '../../shared/Modal';
import { color } from '../../../app/theme';
import { TWD, validURL } from '../../../app/utils';
import Tooltip from '../../shared/Tooltip';
import icHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';

const useStyles = makeStyles(
  () => ({
    text: {
      fontSize: '15px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$13,
      marginBottom: 6,
      '& > span': {
        color: color.$17,
      },
    },
    text2: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$13,
      marginBottom: 12,
      overflowWrap: 'break-word',
      whiteSpace: 'pre-wrap',
    },
    hr: {
      width: '100%',
      height: 1,
      background: color.$9,
      margin: '12px 0',
    },
    totalAddWord: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: 'normal',
      color: color.$12,
      display: 'flex',
      alignItems: 'center',
    },
    word: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$13,
      borderRadius: 8,
      border: `1px solid ${color.$10}`,
      padding: '2px 6px',
    },
    contentEditor: {
      fontSize: '15px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
    },
  }),
  {
    name: 'CorrectionModal',
  },
);

const CorrectionModal = ({ isOpen, onClose, onChange, data }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [response, setResponse] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const { pathKey } = useSelector(store => store.aiBot);

  const renderChannelContent = () => {
    return (
      <div>
        <div className={classes.text}>
          {t(`${pathKey}:correctionRecordsSidebar.question`)}
        </div>
        <div className={classes.text2}>{data?.question}</div>
        <div className={classes.text}>
          {t(`${pathKey}:correctionRecordsSidebar.prevResponse`)}
        </div>
        <div className={classes.text2}>{data?.originalAnswer}</div>
        <hr className={classes.hr} />
        <div className={classes.text}>
          {t(`${pathKey}:correctionRecordsSidebar.response`)}
          <span>*</span>：
        </div>
        <TextEditor
          contentClassName={classes.contentEditor}
          onChange={v => setResponse(v)}
          origriData={response}
          placeholder={t(
            `${pathKey}:correctionRecordsSidebar.responsePlaceholder`,
          )}
          maxLength={1000}
          style={{ width: '100%', marginBottom: 12 }}
          isShowEmoji={false}
          isShowCustomerName={false}
        />
        <div className={classes.text}>
          {t(`${pathKey}:correctionRecordsSidebar.sourceUrl`)}：
        </div>
        <TextFieldCardCounter
          className={classes.input}
          placeholder={t(
            `${pathKey}:correctionRecordsSidebar.sourceUrlPlaceholder`,
          )}
          maxCount={0}
          value={sourceUrl}
          onChange={v => setSourceUrl(v.trim())}
        />
        <hr className={classes.hr} />
        <div className={classes.totalAddWord}>
          {t(`${pathKey}:correctionRecordsSidebar.addWord`)}
          <span className={classes.word}>
            {TWD(
              (response?.length || 0) + (data?.question.length || 0) + 1,
              false,
            ).format()}
          </span>
          <Tooltip
            light
            placement="top"
            title={t(`aiBot:correctionRecordsSidebar.wordTooltip`)}
          >
            <img
              style={{ marginLeft: 4 }}
              src={icHelpCircle}
              alt="plan is expired"
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  const renderChannelAction = () => (
    <>
      <Button
        onClick={() => {
          onClose();
        }}
        type="submit"
        topic="primaryBlue"
      >
        {t('modal.close')}
      </Button>
      <Button
        style={{ marginLeft: 8 }}
        className="actionButton"
        onClick={() => {
          onChange({
            ...data,
            id: data?.id,
            newAnswer: response,
            newSourceUrl: sourceUrl,
          });
        }}
        type="submit"
        topic="primaryBlue"
        disabled={
          !response ||
          response.length > 1000 ||
          (sourceUrl ? !validURL(sourceUrl) : false)
        }
      >
        {t(`${pathKey}:correctionRecordsSidebar.save`)}
      </Button>
    </>
  );

  useEffect(() => {
    if (isOpen) {
      setResponse(data?.newAnswer || '');
      setSourceUrl(data?.newSourceUrl || '');
    }
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <Modal
          style={{ width: '960px' }}
          title={t(`${pathKey}:correctionRecordsSidebar.modalTitle`)}
          content={renderChannelContent()}
          actions={renderChannelAction()}
          open={isOpen}
          handleClose={onClose}
        />
      )}
    </>
  );
};

CorrectionModal.defaultProps = {
  isOpen: 'false',
};

export default CorrectionModal;
